import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

/**
 * A router, in about seventy lines.
 *
 * This site has two real routes and two confirmation pages, none of them
 * parameterised, none of them loading data. React Router is ~15kB gzipped of
 * machinery for matching patterns, resolving relative paths, and coordinating
 * loaders — every one of which is a problem this site does not have. It is
 * also mid-major-churn (v8 at the time of writing, and the SSG plugin
 * ecosystem is still on v6), which is a maintenance cost paid for nothing.
 *
 * So: a path in context, a Link that calls pushState, and a popstate
 * listener. The thing that makes this viable is that prerendering needs the
 * router to accept an initial path as a prop, which is one line here and a
 * whole static-handler API in a real router.
 *
 * What this deliberately does NOT do — because nothing here needs it — is
 * nested routes, route params, search-param state, blocked navigation, or
 * data loading. If any of those ever arrive, replace this file wholesale
 * rather than growing it; the point of it being small is that it is cheap to
 * throw away.
 */

type RouterValue = {
  /** Always normalised: no trailing slash, no query, no hash. */
  path: string;
  navigate: (to: string) => void;
};

const RouterContext = createContext<RouterValue | null>(null);

/**
 * `/contact/` and `/contact?x=1#y` are the same route as `/contact`.
 *
 * Cloudflare's asset server can serve a route with or without the trailing
 * slash, and a prerendered page hydrating at `/contact/` must match the same
 * route the build rendered at `/contact` — otherwise React finds different
 * markup than the HTML it was handed and throws the whole tree away.
 */
export function normalisePath(value: string): string {
  const path = value.split("?")[0].split("#")[0];
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path === "" ? "/" : path;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (value === null) {
    throw new Error("useRouter must be used inside <Router>");
  }
  return value;
}

export function usePath(): string {
  return useRouter().path;
}

export function Router({
  path: initialPath,
  children,
}: {
  path: string;
  children: ReactNode;
}) {
  const [path, setPath] = useState(() => normalisePath(initialPath));

  useEffect(() => {
    // Back and forward. `popstate` does not fire for pushState itself, so
    // this only ever hears the buttons, which is what we want.
    function onPopState() {
      setPath(normalisePath(window.location.pathname));
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((to: string) => {
    const next = normalisePath(to);
    if (next === normalisePath(window.location.pathname)) return;
    window.history.pushState(null, "", to);
    setPath(next);
  }, []);

  const value = useMemo(() => ({ path, navigate }), [path, navigate]);

  return <RouterContext value={value}>{children}</RouterContext>;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

/**
 * A real anchor that happens to navigate without a reload.
 *
 * The `href` is genuine in the served HTML, so crawlers follow it, middle
 * click opens a tab, and with JavaScript off it is an ordinary link to an
 * ordinary prerendered page. Only the plain left click is intercepted — every
 * modifier is the visitor asking for the browser's own behaviour.
 */
export function Link({ href, onClick, target, ...rest }: LinkProps) {
  const { navigate } = useRouter();

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);

    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (target !== undefined && target !== "" && target !== "_self") return;

    event.preventDefault();
    navigate(href);
  }

  return <a href={href} target={target} onClick={handleClick} {...rest} />;
}
